import { UnreachableError } from 'base/helpers';
import { zodParse } from 'base/zod_parse';
import { computed, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import {
  AnyDomainConfig,
  Connection,
  DeltaOf,
  Domain,
  GetterReturnOf,
  MutableOf,
  StateOf,
} from './base';
import { MIN_TIMESAMP } from './clock';
import { DeltaType, ListDelta } from './deltas';

const READONLY_ARRAY_METHODS = [
  'at',
  'concat',
  'entries',
  'every',
  'filter',
  'find',
  'findIndex',
  'flat',
  'flatMap',
  'forEach',
  'includes',
  'indexOf',
  'join',
  'keys',
  'lastIndexOf',
  'map',
  'reduce',
  'reduceRight',
  'slice',
  'some',
  'values',
] as const;
type ReadonlyArrayMethod = (typeof READONLY_ARRAY_METHODS)[number];

type ListMutableBase<I extends AnyDomainConfig> = {
  [Symbol.iterator](): IterableIterator<MutableOf<I>>;
  readonly length: number;
  push(...items: StateOf<I>[]): MutableOf<I>[];
  splice(
    start: number,
    deleteCount: number,
    ...items: StateOf<I>[]
  ): StateOf<I>[];
  clear(): void;
  delete(item: MutableOf<I>): boolean;
  // needed to match the readonly array type
  [Symbol.unscopables]: {};
};

type ListConfig<I extends AnyDomainConfig> = {
  mutableBase: ListMutableBase<I> &
    Pick<MutableOf<I>[], ReadonlyArrayMethod> &
    Pick<GetterReturnOf<I>[], number>;
  state: readonly StateOf<I>[];
  delta: ListDelta<StateOf<I>>;
};

/**
 * List
 *
 * A domain containing a list of items, which are a domain themselves.
 **/
export class ListDomain<I extends AnyDomainConfig>
  implements Domain<ListConfig<I>>
{
  constructor(readonly getInnerDomain: (() => Domain<I>) | Domain<I>) {}

  private get innerDomain(): Domain<I> {
    if (this.getInnerDomain instanceof Function) {
      return this.getInnerDomain();
    }
    return this.getInnerDomain;
  }

  create(
    ...args: Parameters<Domain<ListConfig<I>>['create']>
  ): MutableOf<ListConfig<I>> {
    const mutable = new ListMutableImpl(...args, this.innerDomain);
    return new Proxy(mutable, this.proxyHandler) as typeof mutable &
      Pick<MutableOf<I>, ReadonlyArrayMethod | number> &
      Pick<GetterReturnOf<I>[], number>;
  }

  // Adds the list index signature & readonly methods
  private proxyHandler: ProxyHandler<ListMutableImpl<I>> = {
    get: (target, prop) => {
      const index = typeof prop === 'string' ? parseInt(prop) : NaN;
      if (!Number.isNaN(index)) {
        const m = target.arrayOfMutables[index];
        return m ? target.innerDomain.getter(m) : undefined;
      }
      if (READONLY_ARRAY_METHODS.includes(prop as any)) {
        return (...args: any[]) =>
          (target.arrayOfMutables[prop as ReadonlyArrayMethod] as any)(...args);
      }
      return (target as any)[prop];
    },
    set: (target, prop, value) => {
      const { innerDomain } = target;
      if (!innerDomain.setter) {
        throw new Error('inner domain does not have setter');
      }
      const index = typeof prop === 'string' ? parseInt(prop) : NaN;
      if (Number.isNaN(index)) {
        throw new Error(`Cannot set ${String(prop)} on list mutable`);
      }
      const mutable = target.arrayOfMutables[index];
      if (!mutable) {
        throw new Error(`Index ${index} out of bounds for list mutable`);
      }
      innerDomain.setter(mutable, value);
      return true;
    },
  };

  getter(m: MutableOf<ListConfig<I>>) {
    return m;
  }

  statesEqual(a: StateOf<ListConfig<I>>, b: StateOf<ListConfig<I>>) {
    return (
      a.length === b.length &&
      a.every((v, i) => this.innerDomain.statesEqual(v, b[i]))
    );
  }

  readonly stateSchema = z
    .array(z.lazy(() => this.innerDomain.stateSchema))
    .default([]);

  isMutable(m: unknown): m is MutableOf<ListConfig<I>> {
    return m instanceof ListMutableImpl && m.innerDomain === this.innerDomain;
  }
}

class ListMutableImpl<I extends AnyDomainConfig> implements ListMutableBase<I> {
  readonly mutable = true;
  readonly [Symbol.unscopables] = {};

  private readonly items = new Map<string, MutableOf<I>>();
  private readonly initialItemStates = new Map<string, StateOf<I>>();
  // The children are maintained in descending order of time (newest first)
  //
  // Say we did the opposite (oldest first).  If the initialState was
  // not empty, there'd be an initial delta like:
  //   {afterId: undefined, itemId: 'initial.0', time: min}
  // If we processed the oldest children first, there'd be no way to add
  // an item at the front of the list, because that delta would always win.
  private readonly graph = observable.map<
    string | undefined,
    readonly { childId: string; time: string }[]
  >([], { deep: false });
  private readonly maxEntombedTime = observable.map<string, string>();
  private readonly maxRestoredTime = observable.map<string, string>();

  constructor(
    readonly oid: string,
    initialState: StateOf<ListConfig<I>>,
    private readonly cxn: Connection,
    readonly innerDomain: Domain<I>,
  ) {
    makeObservable(this);
    initialState.forEach((item, i) =>
      this.applyDelta({
        for: oid,
        time: MIN_TIMESAMP,
        type: DeltaType.LIST_INSERT,
        afterId: i === 0 ? undefined : `${oid}.initial.${i - 1}`,
        itemId: `${oid}.initial.${i}`,
        item,
      }),
    );
    this.cxn.register(this, (d) => {
      const isListDelta =
        d.type === DeltaType.LIST_INSERT ||
        d.type === DeltaType.LIST_ENTOMB ||
        d.type === DeltaType.LIST_RESTORE;
      if (!isListDelta) {
        throw new Error(
          `Unexpected delta type ${d.type} in list domain ${this.oid}`,
        );
      }
      this.applyDelta(d);
    });
  }

  private applyDelta(delta: DeltaOf<ListConfig<I>>) {
    // Remember:
    //   - the deltas can come in any order
    switch (delta.type) {
      case DeltaType.LIST_ENTOMB:
        this.applyEntomb(delta);
        break;
      case DeltaType.LIST_RESTORE:
        this.applyRestore(delta);
        break;
      case DeltaType.LIST_INSERT:
        this.applyInsert(delta);
        break;
      default:
        throw new UnreachableError(delta);
    }
  }

  private applyEntomb(
    delta: DeltaOf<ListConfig<I>> & { type: DeltaType.LIST_ENTOMB },
  ) {
    const current = this.maxEntombedTime.get(delta.itemId);
    if (!current || current < delta.time) {
      this.maxEntombedTime.set(delta.itemId, delta.time);
    }
  }

  private applyRestore(
    delta: DeltaOf<ListConfig<I>> & { type: DeltaType.LIST_RESTORE },
  ) {
    const current = this.maxRestoredTime.get(delta.itemId);
    if (!current || current < delta.time) {
      this.maxRestoredTime.set(delta.itemId, delta.time);
    }
    return;
  }

  private applyInsert(
    delta: DeltaOf<ListConfig<I>> & { type: DeltaType.LIST_INSERT },
  ) {
    const initialState = zodParse(this.innerDomain.stateSchema, delta.item);
    if (this.items.has(delta.itemId)) {
      const prev = this.initialItemStates.get(delta.itemId);
      if (!prev) {
        throw new Error(
          `Item ${delta.itemId} has 2 insert deltas, but no initial state`,
        );
      }
      if (!this.innerDomain.statesEqual(prev, initialState)) {
        throw new Error(
          `Item ${delta.itemId} has 2 insert deltas, but different initial states`,
        );
      }
      // Ignore duplicate delta
      return;
    }

    const mutable = this.innerDomain.create(
      delta.itemId,
      initialState,
      this.cxn,
    );
    this.items.set(delta.itemId, mutable);
    this.initialItemStates.set(delta.itemId, initialState);

    const existingChildren = this.graph.get(delta.afterId);
    const newChildren = existingChildren ? [...existingChildren] : [];
    const index = newChildren.findIndex((c) => c.time < delta.time);
    newChildren.splice(index === -1 ? newChildren.length : index, 0, {
      childId: delta.itemId,
      time: delta.time,
    });
    this.graph.set(delta.afterId, newChildren);
  }

  /* Includes tombstones  */
  @computed private get arrayOfIds(): readonly string[] {
    const dfsOrder = new Set<string>();

    const dfs = (id: string | undefined) => {
      if (id != null && dfsOrder.has(id)) return [];
      if (id != null) dfsOrder.add(id);
      const children = this.graph.get(id);
      if (!children) return;
      for (const c of children) {
        dfs(c.childId);
      }
    };

    dfs(undefined);
    const sortedKeys = [...this.graph.keys()].sort();
    for (const start of sortedKeys) {
      dfs(start);
    }

    return [...dfsOrder];
  }

  /* Omits tombstones */
  @computed get arrayOfMutables(): readonly MutableOf<I>[] {
    let mutables: MutableOf<I>[] = [];
    for (const id of this.arrayOfIds) {
      const entombedTime = this.maxEntombedTime.get(id);
      const restoredTime = this.maxRestoredTime.get(id);
      if (
        entombedTime != null &&
        (restoredTime == null || restoredTime < entombedTime)
      ) {
        continue;
      }
      const mutable = this.items.get(id);
      if (mutable) mutables.push(mutable);
    }
    return mutables;
  }

  @computed get state(): StateOf<ListConfig<I>> {
    return this.arrayOfMutables.map((mutable) => mutable.state);
  }

  @computed get visibleOids(): readonly string[] {
    return [
      this.oid,
      ...this.arrayOfMutables.flatMap((mutable) => mutable.visibleOids),
    ];
  }

  @computed get length(): number {
    return this.arrayOfMutables.length;
  }

  [Symbol.iterator](): IterableIterator<MutableOf<I>> {
    return this.arrayOfMutables[Symbol.iterator]();
  }

  splice(
    start: number,
    deleteCount: number,
    ...insertItems: StateOf<I>[]
  ): StateOf<I>[] {
    if (deleteCount < 0) throw new Error('deleteCount must be >= 0');
    if (deleteCount === 0 && insertItems.length === 0) return [];
    if (start < 0) throw new Error('start must be >= 0 (-ve impl todo)');

    const muts = this.arrayOfMutables;
    start = Math.min(start, muts.length);
    deleteCount = Math.min(deleteCount, muts.length - start);

    const deltas: DeltaOf<ListConfig<I>>[] = [];
    const deletedStates: StateOf<I>[] = [];
    for (let i = start; i < start + deleteCount; i++) {
      const { oid, state } = muts[i];
      deletedStates.push(state);
      deltas.push({
        for: this.oid,
        time: this.cxn.clock.advance(),
        type: DeltaType.LIST_ENTOMB,
        itemId: oid,
      });
    }
    let afterId = deltas[deltas.length - 1]?.itemId || muts[start - 1]?.oid;
    for (const item of insertItems) {
      const time = this.cxn.clock.advance();
      deltas.push({
        for: this.oid,
        time,
        type: DeltaType.LIST_INSERT,
        afterId,
        itemId: time,
        item,
      });
      afterId = time;
    }

    this.cxn.fromMutable(...deltas);
    return deletedStates;
  }

  push(...insertItems: StateOf<I>[]): MutableOf<I>[] {
    const originalLength = this.length;
    this.splice(originalLength, 0, ...insertItems);
    const expectedLength = originalLength + insertItems.length;
    const actualLength = this.length;
    if (actualLength !== expectedLength) {
      throw new Error(
        `Push failed (expected ${expectedLength}, got ${actualLength})`,
      );
    }
    return this.arrayOfMutables.slice(originalLength);
  }

  clear(): void {
    this.splice(0, this.length);
  }

  delete(item: MutableOf<I>): boolean {
    const index = this.arrayOfMutables.indexOf(item);
    if (index === -1) return false;
    this.splice(index, 1);
    return true;
  }

  wouldChange(delta: DeltaOf<ListConfig<I>>): boolean {
    const hasEverInserted = this.items.has(delta.itemId);
    if (delta.type === DeltaType.LIST_INSERT) {
      return !hasEverInserted;
    }

    // can not entomb or restore an item that has never been inserted
    if (!hasEverInserted) return false;

    const entombedTime = this.maxEntombedTime.get(delta.itemId);
    const restoredTime = this.maxRestoredTime.get(delta.itemId);
    const currentlyVisible =
      entombedTime == null || (restoredTime || MIN_TIMESAMP) > entombedTime;
    if (delta.type === DeltaType.LIST_ENTOMB) {
      return currentlyVisible && delta.time > (restoredTime || MIN_TIMESAMP);
    } else if (delta.type === DeltaType.LIST_RESTORE) {
      return !currentlyVisible && delta.time > (entombedTime || MIN_TIMESAMP);
    } else {
      throw new UnreachableError(delta);
    }
  }
}
